'use client';

import { useEffect, useRef } from 'react';

import { getClientAnalytics } from '@naumu/analytics/analytics/client-analytics';

/**
 * Resolve `referrer_slug`/`landing` from `document.referrer` at mount.
 *
 * - Empty or external referrer  → landing (came from outside the docs).
 * - Same-origin /docs referrer  → the referring docs slug ('home' for /docs,
 *   'category:<cat>' for a category page, the article slug otherwise).
 * - Same-origin non-docs page   → no docs referrer, but not a landing.
 */
const readDocsReferrer = (): {
	referrerSlug: string | null;
	landing: boolean;
} => {
	if (typeof document === 'undefined' || typeof window === 'undefined') {
		return { referrerSlug: null, landing: false };
	}

	const ref = document.referrer;
	if (!ref) return { referrerSlug: null, landing: true };

	let url: URL;
	try {
		url = new URL(ref);
	} catch {
		return { referrerSlug: null, landing: true };
	}

	if (url.origin !== window.location.origin) {
		return { referrerSlug: null, landing: true };
	}

	// Same-origin: trailing-slash-insensitive path match against /docs.
	const path = url.pathname.replace(/\/+$/, '');
	if (path !== '/docs' && !path.startsWith('/docs/')) {
		return { referrerSlug: null, landing: false };
	}
	if (path === '/docs') return { referrerSlug: 'home', landing: false };

	const rest = path.slice('/docs/'.length);
	if (rest.startsWith('category/')) {
		return {
			referrerSlug: `category:${rest.slice('category/'.length)}`,
			landing: false,
		};
	}
	return { referrerSlug: rest, landing: false };
};

/**
 * Fires `docs_article_viewed` exactly once per article mount on the public docs
 * site (mounted by the article + changelog server pages; changelog passes
 * slug 'changelog'). Renders nothing. Degrades to a silent no-op when no
 * analytics client is registered.
 */
export const DocsAnalyticsTracker = ({
	slug,
	group,
}: {
	slug: string;
	group: string;
}): null => {
	const fired = useRef(false);

	useEffect(() => {
		// StrictMode double-mounts effects in dev; the ref guarantees one fire.
		if (fired.current) return;
		fired.current = true;

		const { referrerSlug, landing } = readDocsReferrer();
		getClientAnalytics().track('docs_article_viewed', {
			slug,
			group,
			referrer_slug: referrerSlug,
			landing,
		});
	}, [slug, group]);

	return null;
};
